import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostBinding,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { DomSanitizer, SafeUrl, Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';
import { Subscription, filter, map, of, startWith, switchMap, tap } from 'rxjs';
import { version } from '../../package.json';
import {
  APPLICATION_NAME,
  ESQEP_AUTH_TYPE,
  IDLE,
  TIME_OUT,
} from '@core/constants/constants.component';
import { DrawerAutosizeHackDirective } from '@core/directives/DrawerAutosize.directive';
import { AlertData, AlertLevels } from '@core/services/alert/alert.service';
import { AuthService } from '@core/services/auth/auth.service';
import { AUTHENTICATION_CONFIGURATION_NAMES } from '@core/services/auth/authenticationConfigNames';
import { ConfigService } from '@core/services/config/config.service';
import { DialogService } from '@core/services/dialog/dialog.service';
import { IconService } from '@core/services/icon/icon.service';
import { MenuService } from '@core/services/menu/menu.service';
import { BrowserStorageService } from '@core/utilities/browser-storage/browser-storage.service';
import { AlertComponent } from '@feature/alert/alert.component';
import { SpinnerService } from '@ui/controls/services/spinner.service';
import { CustomSidenavComponent } from '@ui/custom-sidenav/custom-sidenav.component';
import { FooterComponent } from '@ui/footer/footer.component';
import { HeaderComponent } from '@ui/header/header.component';
import { InactivityComponent } from '@ui/inactivity/inactivity.component';

const idle = signal<number>(IDLE);
const timeout = signal<number>(TIME_OUT);

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterModule,
    AlertComponent,
    RouterOutlet,
    RouterModule,
    FooterComponent,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    HeaderComponent,
    CustomSidenavComponent,
    MatDialogModule,
    NgIdleKeepaliveModule,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    {
      provide: MatDialogRef,
      useValue: {},
    },
  ],
})
export class AppComponent implements OnDestroy, OnInit {
  // loginPageMessage =
  //   environment?.loginPageMessage || 'Welcome to your eSQEP Portal';
  @HostBinding('attr.app-version') version = version;
  private readonly idle = inject(Idle);
  private loading: boolean = false;
  private logging: any;
  private authStatusSubs = new Subscription();
  private userGroupSubs = new Subscription();
  private appSubscriptions = new Subscription();
  private isIframe: boolean = false;
  private navigationSubscription: Subscription;
  private readonly destroyRef = inject(DestroyRef);
  private readonly homeMenu = inject(MenuService);
  private readonly storageService = inject(BrowserStorageService);
  private readonly configService = inject(ConfigService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly broadcastService = inject(MsalBroadcastService, {
    optional: true,
  });
  private readonly oktaStateService = inject(OktaAuthStateService, {
    optional: true,
  });
  private readonly iconService = inject(IconService);
  private idleState: 'NOT_STARTED' | 'NOT_IDLE' | 'IDLE' | 'TIMED_OUT' =
    'NOT_STARTED';
  private oktaAuth = inject(OKTA_AUTH, { optional: true });
  private dialogRef = inject(MatDialogRef<InactivityComponent>);
  private domSanitizer = inject(DomSanitizer);

  protected readonly title = signal<string>('');
  protected readonly helpUrl = signal<SafeUrl | undefined>(undefined);
  protected readonly isSignedIn = signal<boolean>(false);
  protected readonly appVersion = signal<string>('');
  protected readonly applicationName = signal<string>(APPLICATION_NAME);
  protected readonly dialog = inject(MatDialog);
  protected readonly dialogService = inject(DialogService);
  protected readonly authService = inject(AuthService);
  protected readonly router = inject(Router);
  protected readonly collapsed = signal<boolean>(false);
  protected readonly sidenavWidth = '250px';

  exceptionData = {
    PERSON_UPN: {
      level: AlertLevels.ERROR,
      code: 'APC-001',
      message: 'Unable to retrieve user data',
    } as AlertData,
    USER_GROUP: {
      level: AlertLevels.ERROR,
      code: 'APC-003',
      message: 'Unable to retrieve user group info',
    } as AlertData,
    ADMIN_STATUS: {
      level: AlertLevels.ERROR,
      code: 'APC-004',
      message: 'Unable to retrieve user role info',
    } as AlertData,
    PERSON_UUID: {
      level: AlertLevels.ERROR,
      code: 'APC-005',
      message: 'Unable to retrieve user data',
    } as AlertData,
    SECURITY_ROLE: {
      level: AlertLevels.ERROR,
      code: 'APC-006',
      message: 'Unable to retrieve user security role',
    } as AlertData,
    PERSON_PHOTO: {
      level: AlertLevels.WARNING,
      code: 'APC-007',
      message: 'Unable to retrieve user photo',
    } as AlertData,
  };

  constructor() {
    this.iconService.registerIcons();
    this.idle.setIdle(this.idle_time ? this.idle_time : idle());
    this.idle.setTimeout(this.time_out ? this.time_out : timeout());
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.onIdleStart
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.idleState = 'IDLE';
        const config: MatDialogConfig<{}> = {
          width: '530px',
          height: '250px',
          hasBackdrop: true,
          disableClose: true,
          data: {
            title: 'Inactivity Warning',
            inactiveTime: this.time_out ? this.time_out : timeout(),
          },
        };
        this.dialogRef = this.dialogService.open(InactivityComponent, config);

        this.dialogRef
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response: unknown) => {
            if (response) {
              this.logout();
            } else {
              this.authService.authTimeoutSubj.next(true);
              this.reset();
            }
          });
      });

    this.idle.onIdleEnd
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.idleState = 'NOT_IDLE';
      });

    this.idle.onTimeoutWarning
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((secondsLeft: number) => {
        if (this.dialogRef && this.dialogRef.componentInstance) {
          this.dialogRef.componentInstance.updateData(secondsLeft);
        }
      });

    this.idle.onTimeout
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.idleState = 'TIMED_OUT';
        this.logout();
      });
  }

  private reset() {
    this.idle.watch();
    this.idleState = 'NOT_IDLE';
  }

  get idle_time(): number {
    return this.configService.idle;
  }

  get time_out(): number {
    return this.configService.time_out;
  }

  get spinnerInProgressUrls(): string {
    return this.spinnerService.inProgressUrls();
  }

  get inUserGroup(): boolean {
    return this.authService.inUserGroup;
  }

  get hasAdminRole(): boolean {
    return this.authService.hasAdminRole;
  }

  get clientLogoAsset(): string {
    return this.configService.clientLogoAsset;
  }

  get clientLogoAsset2(): string {
    return this.configService.clientLogoAsset2;
  }

  get clientHeading(): string {
    return this.configService.heading;
  }

  get clientSubTitle(): string {
    return this.configService.subTitle;
  }

  get navbarBgClass(): string {
    return this.configService.navbarBgClass;
  }

  clog = (msg: string, lvl: string | number) => {
    this.configService.logging.newMsg(msg, lvl);
  };

  async ngOnInit() {
    this.reset();
    this.setupNavigationListener();
    this.isIframe = window !== window.parent && !window.opener;

    if (window !== window.parent && !window.opener) {
      this.clog('In iframe', 'INFO'); // For token refresh purposes, prevent multiple iframes from being created
    } else {
      this.spinnerService.reset();
      this.logging = this.configService.logging;
      this.appVersion.set(this.configService.appVersion);

      if (
        this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.ENTRA]
          .enabled
      ) {
        await this.authService.msalService?.instance.initialize();
        await this.authService.msalService?.instance.handleRedirectPromise();

        this.broadcastService?.msalSubject$.pipe().subscribe((result) => {
          if (result.eventType === EventType.LOGIN_FAILURE) {
            this.router.navigate(['/login']);
          }

          this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((url: NavigationEnd) => {
              if (url.url === '/') {
                // we need this because msal:loginFailure triggers Router Event: NavigationCancel with url set to be '/'
                // (msal post logout url is the domain without the '/home')
                this.router.navigate(['/home']);
              }

              window.scrollTo(0, 0);
            });
        });
        this.broadcastService?.inProgress$
          .pipe(
            filter(
              (status: InteractionStatus) => status === InteractionStatus.None,
            ),
          )
          .subscribe((s) => {
            if (
              this.storageService.get(ESQEP_AUTH_TYPE) ===
              AUTHENTICATION_CONFIGURATION_NAMES.ENTRA
            ) {
              this.authService.isAuthenticated =
                this.authService.msalService.instance.getAllAccounts().length >
                0;
            }
          });
      }

      if (
        this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA]
          .enabled &&
        this.storageService.get(ESQEP_AUTH_TYPE) ===
        AUTHENTICATION_CONFIGURATION_NAMES.OKTA
      ) {
        this.oktaStateService.authState$
          .pipe(
            filter((s: AuthState) => !!s),
            map((s: AuthState) => s.isAuthenticated ?? false),
          )
          .subscribe((s) => {
            if (s) {
              this.authService.isAuthenticated = true;
            }
          });
      }
      this.authStatusSubs = this.authService.authenticationStatusSubj.subscribe(
        (a) => {
          if (
            this.configService.authSetup[
              AUTHENTICATION_CONFIGURATION_NAMES.ENTRA
              ] &&
            this.configService.authSetup[
              AUTHENTICATION_CONFIGURATION_NAMES.ENTRA
              ].enabled
          ) {
            //const isSignedIn =  a || this.authService.msalService?.instance.getAllAccounts().length > 0;
            const isSignedIn = a;

            this.isSignedIn.set(isSignedIn);
          } else {
            this.isSignedIn.set(a);
          }
          this.clog('Auth Status:' + this.isSignedIn(), 'VERBOSE');
          if (this.authService.isAuthenticated) {
            this.authService.populateUserPermissions();
            //this.authService.populateSecurityRoles();
          }
        },
      );
    }
  }

  ngOnDestroy() {
    this.authStatusSubs.unsubscribe();
    this.appSubscriptions.unsubscribe();
    this.navigationSubscription.unsubscribe();
  }

  onRouterOutletActivate(event: any) {
    this.configService.componentName = event.constructor.name;
  }

  logout() {
    this.authService.logout();
  }

  getPageRouteData(): {
    title?: string;
    itemTranslationKey?: string;
    helpUrl?: string;
  } {
    let child = this.activatedRoute.firstChild;
    if (!child) {
      return this.activatedRoute.snapshot.data;
    }
    while (child.firstChild) {
      child = child.firstChild;
    }
    if (child.snapshot.data.title) {
      return child.snapshot.data;
    }
    return null;
  }

  private setupNavigationListener() {
    this.navigationSubscription = this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        tap((event) => (this.loading = event instanceof NavigationStart)),
        filter((event) => event instanceof NavigationEnd),
        map(() => this.getPageRouteData()),
        switchMap((routeData) => {
          // removed whislt resetting the translation stuff
          // if (routeData?.itemTranslationKey) {
          //   return this.translocoService
          //     .selectTranslate(routeData.itemTranslationKey)
          //     .pipe(map((title) => ({ ...routeData, title })));
          // } else {
          return of({ ...routeData });
          // }
        }),
        startWith(null),
      )
      .subscribe((data) => {
        this.title.set(data?.title ?? null);
        this.titleService.setTitle(this.title() ?? APPLICATION_NAME);
        if (data?.helpUrl !== undefined) {
          this.helpUrl.set(
            this.domSanitizer.bypassSecurityTrustUrl(data?.helpUrl),
          );
        } else {
          this.helpUrl.set(
            'https://scribehow.com/page/eSQEP_Guides__v2NOaalmRu6P9-aC-DJqkw',
          );
        }
      });
  }
}
